import * as React from "react"
import { FEED, type FeedItem } from "./items"
import { Sections } from "../work/sections"
import "./feed.css"

/** The feed (a prototype): a masonry grid of whatever's been made, any shape.
 *  Each tile keeps its image's proportions; they go, in order, into the
 *  shortest column (as Pinterest does). 4 columns up to 1680 wide, 5 beyond
 *  (fewer on small screens); a 10px gutter; tiles rise in as they come. */

const GUTTER = 10
const columnsFor = (w: number) => (w > 1680 ? 5 : w >= 1024 ? 4 : w >= 700 ? 3 : 2)

function useColumns() {
    const [n, setN] = React.useState(() => columnsFor(innerWidth))
    React.useEffect(() => {
        const on = () => setN(columnsFor(innerWidth))
        addEventListener("resize", on)
        return () => removeEventListener("resize", on)
    }, [])
    return n
}

/** in order, each into the column that's shortest so far */
function lay(items: FeedItem[], n: number) {
    const cols: { item: FeedItem; i: number }[][] = Array.from({ length: n }, () => [])
    const heights = new Array(n).fill(0)
    items.forEach((item, i) => {
        let c = 0
        for (let k = 1; k < n; k++) if (heights[k] < heights[c] - 1e-6) c = k
        cols[c].push({ item, i })
        heights[c] += item.h / item.w + GUTTER / 400
    })
    return cols
}

function Tile({ item, col }: { item: FeedItem; col: number }) {
    const ref = React.useRef<HTMLDivElement>(null)
    React.useEffect(() => {
        const el = ref.current!
        // (comes in once it's a little way onto the screen, every time: rising
        // from below scrolling down, dropping in from above scrolling up; the
        // ones coming together a beat apart, left to right. Off the screen
        // again, it's set back, ready to come in again.)
        const io = new IntersectionObserver(
            ([e]) => {
                if (e.isIntersecting) {
                    el.style.setProperty("--d", `${col * 70}ms`)
                    el.setAttribute("data-in", "")
                } else {
                    el.style.setProperty("--from", e.boundingClientRect.top < 0 ? "-56px" : "56px")
                    el.removeAttribute("data-in")
                }
            },
            { rootMargin: "0px 0px -6% 0px" }
        )
        io.observe(el)
        return () => io.disconnect()
    }, [])
    return (
        <div ref={ref} className="fd-tile" style={{ aspectRatio: `${item.w} / ${item.h}`, ["--tone" as any]: item.tone }}>
            <img
                src={item.src}
                width={item.w}
                height={item.h}
                alt={item.alt || ""}
                loading="lazy"
                decoding="async"
                draggable={false}
                ref={(el) => el && el.complete && el.naturalWidth && el.setAttribute("data-loaded", "")}
                onLoad={(e) => e.currentTarget.setAttribute("data-loaded", "")}
            />
        </div>
    )
}

export default function FeedPage() {
    const root = React.useRef<HTMLDivElement>(null)
    const n = useColumns()
    const cols = React.useMemo(() => lay(FEED, n), [n])

    React.useLayoutEffect(() => {
        // the logo and local time over the tiles: white, blended by difference
        // (as on the about page: html[data-av], about.css)
        const html = document.documentElement
        html.setAttribute("data-av", "")
        html.setAttribute("data-feed", "")
        const release = () => (html.removeAttribute("data-av"), html.removeAttribute("data-feed"))
        // (the page change out of here dissolves it)
        const list = [root.current!]
        Sections.set(list)
        Sections.leave = release
        return () => {
            Sections.clear(list)
            if (Sections.leave === release) Sections.leave = null
            release()
        }
    }, [])

    return (
        <div ref={root} className="fd" data-nto-scene="" style={{ ["--gutter" as any]: GUTTER + "px" }}>
            <div className="fd-grid">
                {cols.map((col, c) => (
                    <div key={c} className="fd-col">
                        {col.map(({ item, i }) => (
                            <Tile key={i} item={item} col={c} />
                        ))}
                    </div>
                ))}
            </div>
        </div>
    )
}
